/**
 * `distribution` template — a live histogram of one numeric field over a cohort, with
 * `highlight_range` shading the bins it covers. Always live: distributions carry no `data`
 * fallback in the contract. A y-axis (count) and x-axis (the field's own bin boundaries, in the
 * same units the rest of the product uses) run alongside the bars; hovering or focusing a bin
 * shows its exact range and count.
 */

import { useState } from "react";

import { fmtVel } from "@/features/sku-velocity/field/util";
import type { DistributionView } from "@/lib/api";
import { fmtMoneyShort, type Sku } from "@/lib/data";
import { binDistribution, evalCohort } from "@/lib/specs/evaluate";

import { niceTicks } from "./axis";
import { ChartTooltip } from "./chart-tooltip";
import { HIGHLIGHT_PRIMARY, NEUTRAL } from "./colors";
import type { ViewChrome, ViewSize } from "./view-shell";

/** What each histogrammable field IS, in the words the rest of the product uses — the x-axis
 *  title, so the reader isn't left decoding a schema name like `lastMovedQ`. */
const FIELD_LABEL: Record<NonNullable<DistributionView["field"]>, string> = {
  vel: "velocity (boxes/mo)",
  inv: "inventory (boxes held)",
  value: "annual NTS value",
  price: "unit price per box",
  faces: "estimated pick-face depth",
  lastMovedQ: "quarters since last shipped",
  accounts: "accounts ordering the SKU",
};

function formatField(field: DistributionView["field"], v: number): string {
  switch (field ?? "vel") {
    case "vel":
      return fmtVel(v);
    case "value":
    case "price":
      return fmtMoneyShort(v);
    default:
      return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
}

interface DistributionChartProps {
  spec: DistributionView;
  size: ViewSize;
  skus: readonly Sku[];
  chrome?: ViewChrome;
}

export function DistributionChart({ spec, size, skus, chrome = "full" }: DistributionChartProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const cohortSkus = spec.cohort ? evalCohort(spec.cohort, skus) : skus;
  const field = spec.field ?? "vel";
  const values = cohortSkus.map((s) => s[field]);
  const bins = binDistribution(values, spec.bins ?? 30, spec.scale ?? "log");
  const max = Math.max(1, ...bins.map((b) => b.count));
  const [rangeLo, rangeHi] = spec.highlight_range ?? [];

  if (!bins.length) {
    return <p className="text-muted-foreground text-xs">No SKUs in this cohort.</p>;
  }

  const isCard = size === "card";
  const height = isCard ? 90 : 160;
  const yTicks = niceTicks(max, isCard ? 2 : 4).filter((t) => t <= max);
  // A handful of evenly spaced bin-boundary labels along the x axis — every bin's own edge would
  // overlap into unreadable clutter, so pick roughly `xTickCount` of them by index.
  const xTickCount = isCard ? 3 : 5;
  const xTickIndices = Array.from({ length: xTickCount }, (_, i) =>
    Math.round((i * (bins.length - 1)) / Math.max(1, xTickCount - 1)),
  );
  const hovered = hoverIndex !== null ? bins[hoverIndex] : null;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 gap-1">
        {/* y-axis title, rotated into the left margin beside its own tick column */}
        <div className="text-muted-foreground flex w-3.5 shrink-0 items-center" style={{ height }}>
          <span className="rotate-180 text-[10px] whitespace-nowrap [writing-mode:vertical-rl]">
            SKUs
          </span>
        </div>
        <div
          className="text-muted-foreground flex shrink-0 flex-col justify-between text-right text-[10px] tabular-nums"
          style={{ height }}
        >
          {[...yTicks].reverse().map((t) => (
            <span key={t}>{t.toLocaleString()}</span>
          ))}
        </div>
        <div className="border-border relative min-w-0 flex-1 border-b border-l">
          <div className="flex h-full items-end gap-px" style={{ height }}>
            {bins.map((b, i) => {
              const inRange =
                rangeLo !== undefined && rangeHi !== undefined && b.x1 > rangeLo && b.x0 < rangeHi;
              const clear = () => setHoverIndex((cur) => (cur === i ? null : cur));
              return (
                <div
                  key={i}
                  role="img"
                  aria-label={`${formatField(field, b.x0)}–${formatField(field, b.x1)}: ${b.count} SKUs`}
                  tabIndex={0}
                  className="min-w-[2px] flex-1 cursor-default rounded-t outline-none"
                  style={{
                    height: `${Math.max(2, (b.count / max) * 100)}%`,
                    backgroundColor: inRange ? HIGHLIGHT_PRIMARY : NEUTRAL,
                  }}
                  onMouseEnter={() => setHoverIndex(i)}
                  onMouseLeave={clear}
                  onFocus={() => setHoverIndex(i)}
                  onBlur={clear}
                />
              );
            })}
          </div>
          {hovered ? (
            <ChartTooltip x={`${((hoverIndex! + 0.5) / bins.length) * 100}%`} y="0%">
              <div className="font-semibold">
                {formatField(field, hovered.x0)}–{formatField(field, hovered.x1)}
              </div>
              <div>{hovered.count.toLocaleString()} SKUs</div>
            </ChartTooltip>
          ) : null}
        </div>
      </div>
      <div className="flex min-w-0 gap-1">
        {/* Matches the y-title column + the tick column above, so the x labels stay under the
            bars they belong to. */}
        <span className="w-3.5 shrink-0" aria-hidden />
        <span className="shrink-0" style={{ width: "2.5rem" }} aria-hidden />
        <div className="relative min-w-0 flex-1">
          {xTickIndices.map((i) => (
            <span
              key={i}
              className="text-muted-foreground absolute top-0 -translate-x-1/2 text-[10px] tabular-nums first:translate-x-0 last:-translate-x-full"
              style={{ left: `${((i + 0.5) / bins.length) * 100}%` }}
            >
              {formatField(field, bins[i]!.x0)}
            </span>
          ))}
        </div>
      </div>
      <p className="text-muted-foreground mt-1 text-center text-[10px]">
        {FIELD_LABEL[field]} · {spec.scale ?? "log"} scale
      </p>
      {chrome === "bare" ? null : (
        <p className="text-muted-foreground text-[11px]">
          {cohortSkus.length.toLocaleString()} SKUs
        </p>
      )}
    </div>
  );
}
